import Component from "@base/Component.js";
import CockpitPanel from "./CockpitPanel.js";
import t from "@/services/translationService.js";
import tagController from "@modules/tag/tagController.js";
import TagEditModal from "@modules/tag/TagEditModal.js";
import { editButton } from "./rowActions.js";

/**
 * Cockpit → Tag Manager. The vocabulary behind the gallery filter: every tag,
 * its name in both languages, and how many galleries carry it.
 *
 * Most tags are born in the gallery editor — typing one mints it (tagService
 * .ensure) — but they can also be created here up front, ready to be used and
 * already translated. Beyond that this panel is where the vocabulary is kept:
 * rename, merge two tags that mean the same thing, delete one that shouldn't
 * exist.
 *
 * Deleting is not offered on the row. It happens inside the editor, like a
 * gallery's or a gear item's, so it takes opening the thing first rather than
 * one stray click in a list.
 *
 * props: { lang }
 */
export default class TagManagerPanel extends CockpitPanel {
  async onBeforeMount() {
    await this.load();
  }

  async load() {
    try {
      tagController.invalidate(); // counts move whenever a gallery is saved
      await tagController.ensureLoaded();
      this.state.tags = tagController.manager.all();
      this.state.error = "";
    } catch (err) {
      this.state.tags = [];
      this.state.error = err.message || t.t("common.error");
    }
  }

  title() {
    return Component.escape(t.t("cockpit.nav.tags"));
  }

  lead() {
    return Component.escape(t.t("cockpit.tags.lead"));
  }

  actions() {
    return `<button type="button" class="btn-primary" data-action="add">+ ${Component.escape(t.t("cockpit.tags.add"))}</button>`;
  }

  body() {
    if (this.state.error) return CockpitPanel.error(this.state.error);

    const tags = this.state.tags || [];
    if (!tags.length) return CockpitPanel.empty(t.t("cockpit.tags.empty"));

    const rows = tags
      .map((tag) => {
        // A tag nothing carries any more is worth pointing at: it still shows
        // up in nothing but this list, and it is exactly what delete is for.
        const count = tag.count
          ? `<span class="cockpit-row__count">${tag.count}</span>`
          : `<span class="cockpit-row__muted">${Component.escape(t.t("cockpit.tags.unused"))}</span>`;
        return `
          <tr>
            <td><span class="cockpit-row__name">${Component.escape(tag.nameEn)}</span></td>
            <td>${Component.escape(tag.nameDe)}</td>
            <td><span class="cockpit-row__code">${Component.escape(tag.key)}</span></td>
            <td class="cockpit-row__nowrap">${count}</td>
            <td class="cockpit-table__actions">${editButton(tag.key)}</td>
          </tr>`;
      })
      .join("");

    return `
      <div class="cockpit-table__wrap">
        <table class="cockpit-table">
          <thead>
            <tr>
              <th>${Component.escape(t.t("cockpit.tags.colEn"))}</th>
              <th>${Component.escape(t.t("cockpit.tags.colDe"))}</th>
              <th>${Component.escape(t.t("cockpit.tags.colKey"))}</th>
              <th>${Component.escape(t.t("cockpit.tags.colUsed"))}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  onMounted() {
    const add = this.$('[data-action="add"]');
    if (add) this.listen(add, "click", () => this.#open(null));

    this.$$("[data-edit]").forEach((btn) =>
      this.listen(btn, "click", () => this.#open(this.state.tags.find((row) => row.key === btn.getAttribute("data-edit"))))
    );
  }

  /** Open the tag editor — null creates. Delete lives in there too. */
  #open(tag) {
    new TagEditModal({ tag: tag || null, onSaved: () => this.reload() }).mount(document.body);
  }

  async reload() {
    await this.load();
    this.setState({});
  }
}
